<script setup lang="ts">
import merge from 'lodash/merge';

interface FormMatchingPairs
{
    content: {
        value: {
            description: string;
            questions: Pair[];
        };
    };
}

interface Pair
{
    id: number;
    question: string;
    answer: string;
}

interface Props
{
    modelValue: any;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:modelValue': [value: any];
}>();

const defaults: any = {
    description: '',
    questions: [] as Pair[]
};

const form = computed((): FormType<FormMatchingPairs> =>
{
    return props.modelValue;
});

function addQuestion(): void
{
    const ids = form.value.content.value.questions.map((x: { id: any }) => x.id);

    const maxId = form.value.content.value.questions.length > 0 ? Math.max(...ids) : 1;

    form.value.content.value.questions.push({
        id: maxId + 1,
        question: '',
        answer: ''
    });
}

function removeQuestion(index: number): void
{
    const items = form.value.content.value.questions.slice();

    items.splice(index, 1);
    form.value.content.value.questions = items;

    form.value.$errors.clear();
}

function questionUp(index: number): void
{
    if (index > 0)
    {
        const questions = form.value.content.value.questions.splice(index, 1);

        form.value.content.value.questions.splice(index - 1, 0, questions[0]);

        form.value.$errors.clear();
    }
}

function questionDown(index: number): void
{
    if (index < form.value.content.value.questions.length - 1)
    {
        const questions = form.value.content.value.questions.splice(index, 1);

        form.value.content.value.questions.splice(index + 1, 0, questions[0]);

        form.value.$errors.clear();
    }
}

function triggetModel(): any
{
    return form.value;
}

onBeforeMount((): void =>
{
    form.value.content.value = merge(defaults, form.value.content.value);
    form.value.file = null;

    if (!form.value.content.value.questions.length)
    {
        form.value.content.value.questions = [{
            id: 1,
            question: '',
            answer: ''
        }];
    }
});

watch(() => form.value.content.value, (value: any): void =>
{
    emit('update:modelValue', triggetModel());
}, { deep: true });
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Opis]]]')" :invalid-feedback="form.$errors.first('content.value.description')" :state="form.$errors.state('content.value.description')">
        <RichTextEditor
            :id="seconds('description-' + form.id)"
            v-model="form.content.value.description"
            name="description"
            shortcode
            timeline
        />
    </IdeoFormGroup>
    <IdeoFormGroup :invalid-feedback="form.$errors.first('content.value.questions.answer')" :state="form.$errors.state('content.value.questions.answer')">
        <div class="row">
            <div class="col-md-5">
                {{ $t('[[[Element]]]') }}
            </div>
            <div class="col-md-5">
                {{ $t('[[[Dopasowanie]]]') }}
            </div>
        </div>
        <div v-for="(question, i) in form.content.value.questions" :key="i">
            <div :class="['row p-2 mb-3', { 'bg-light': i % 2 === 0 }]">
                <div class="col-md-5">
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <span class="input-group-text bg-primary border-primary text-white">{{ i + 1 }}</span>
                        </div>
                        <IdeoFormTextarea v-model="question.question" :rows="2" :name="`content.value.questions[${i}].question`" :data-cy="`question-${i}`" />
                    </div>
                    <small v-if="!form.$errors.state(`content.value.questions[${i}].question`)" class="form-text text-danger">{{ form.$errors.first(`content.value.questions[${i}].question`) }}</small>
                </div>
                <div class="col-md-5">
                    <div class="input-group">
                        <div class="input-group-prepend">
                            <span class="input-group-text bg-primary border-primary text-white">{{ i + 1 }}</span>
                        </div>
                        <IdeoFormTextarea v-model="question.answer" :rows="2" :name="`content.value.questions[${i}].answer`" :data-cy="`answer-${i}`" />
                    </div>
                    <small v-if="!form.$errors.state(`content.value.questions[${i}].answer`)" class="form-text text-danger">{{ form.$errors.first(`content.value.questions[${i}].answer`) }}</small>
                </div>
                <div class="col-md-2 d-flex align-items-start">
                    <ActionButton
                        variant="danger"
                        icon="far fa-trash"
                        class="ms-auto"
                        :title="$t('[[[Usuń pytanie]]]')"
                        :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                        :disabled="form.content.value.questions.length < 2"
                        @click="removeQuestion(i)"
                    />
                    <ActionButton variant="secondary" icon="far fa-arrow-up" :title="$t('[[[Przenieś wyżej]]]')" :disabled="i == 0" @click="questionUp(i)" />
                    <ActionButton variant="secondary" icon="far fa-arrow-down" :title="$t('[[[Przenieś niżej]]]')" :disabled="i == form.content.value.questions.length - 1" @click="questionDown(i)" />
                </div>
            </div>
            <hr>
        </div>
        <div>
            <ActionButton
                variant="outline-dark"
                class="w-100"
                icon="far fa-plus"
                :text="$t('[[[Nowe połączenie]]]')"
                data-cy="addQuestion"
                @click="addQuestion()"
            />
        </div>
    </IdeoFormGroup>
</template>
